import React, { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { api, absoluteUploadUrl } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowLeft, MessageCircle, Phone, Mail, Trash2 } from "lucide-react";
import { ADMIN } from "@/constants/testIds";
import { waLink } from "@/config/brand";

const STATUSES = ["New", "Contacted", "Quoted", "Booked", "Completed", "Lost"];

export default function AdminLeadDetail() {
    const { id } = useParams();
    const nav = useNavigate();
    const [data, setData] = useState(null);
    const [noteBody, setNoteBody] = useState("");
    const [status, setStatus] = useState("");
    const [statusNote, setStatusNote] = useState("");
    const [quotedPrice, setQuotedPrice] = useState("");
    const [bookingValue, setBookingValue] = useState("");

    const load = async () => {
        const res = await api.get(`/admin/leads/${id}`);
        setData(res.data);
        setStatus(res.data.lead.status);
        setQuotedPrice(res.data.lead.quoted_price ?? "");
        setBookingValue(res.data.lead.booking_value ?? "");
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

    if (!data) return <div className="text-sm text-[#17252D]/60">Loading…</div>;
    const l = data.lead;

    const updateStatus = async () => {
        try {
            await api.patch(`/admin/leads/${id}/status`, { status, note: statusNote });
            setStatusNote("");
            await load();
            toast.success("Status updated");
        } catch (e) {
            toast.error("Failed to update status");
        }
    };
    const savePricing = async () => {
        try {
            await api.patch(`/admin/leads/${id}/pricing`, {
                quoted_price: quotedPrice === "" ? null : Number(quotedPrice),
                booking_value: bookingValue === "" ? null : Number(bookingValue),
            });
            await load();
            toast.success("Pricing saved");
        } catch (e) {
            toast.error("Failed to save pricing");
        }
    };
    const addNote = async () => {
        if (!noteBody.trim()) return;
        try {
            await api.post(`/admin/leads/${id}/notes`, { body: noteBody });
            setNoteBody("");
            await load();
            toast.success("Note added");
        } catch {
            toast.error("Could not add note");
        }
    };
    const removeLead = async () => {
        if (!window.confirm("Delete this lead permanently?")) return;
        await api.delete(`/admin/leads/${id}`);
        nav("/admin/leads");
    };

    return (
        <div className="space-y-4">
            <div className="flex items-center gap-2">
                <Link to="/admin/leads" className="inline-flex items-center gap-1 text-sm text-[#001B3D]"><ArrowLeft className="h-4 w-4" /> All leads</Link>
                <div className="ml-auto flex items-center gap-2">
                    <a href={`tel:${l.phone}`}><Button variant="outline" size="sm"><Phone className="h-4 w-4 mr-2" /> Call</Button></a>
                    <a href={waLink(`Hi ${l.full_name}, regarding your quote request ${l.reference}…`)} target="_blank" rel="noreferrer"><Button variant="outline" size="sm" className="border-[#25D366] text-[#128C7E] hover:bg-[#25D366]/10"><MessageCircle className="h-4 w-4 mr-2" /> WhatsApp</Button></a>
                    <a href={`mailto:${l.email}?subject=YS Removals — ${l.reference}`}><Button variant="outline" size="sm"><Mail className="h-4 w-4 mr-2" /> Email</Button></a>
                    <Button variant="outline" size="sm" onClick={removeLead} className="border-[#17252D]/30 text-[#17252D]"><Trash2 className="h-4 w-4 mr-2" /> Delete</Button>
                </div>
            </div>

            <div className="grid gap-4 lg:grid-cols-3">
                <div className="lg:col-span-2 space-y-4">
                    <div className="rounded-xl bg-white border border-[#17252D]/10 p-5">
                        <div className="flex items-center gap-3">
                            <div className="inline-flex h-10 w-10 items-center justify-center rounded-lg bg-[#001B3D]/10 text-[#001B3D] text-xs font-mono">{l.reference.slice(-4)}</div>
                            <div>
                                <div className="font-heading text-xl font-semibold">{l.full_name}</div>
                                <div className="text-xs text-[#17252D]/60">{l.reference} — {(l.created_at || "").slice(0, 16).replace("T", " ")}</div>
                            </div>
                            <span className="ml-auto text-xs px-2 py-1 rounded-full border border-[#001B3D]/25 text-[#001B3D]">{l.status}</span>
                        </div>
                        <div className="mt-4 grid gap-3 sm:grid-cols-2 text-sm">
                            <Field k="Phone" v={l.phone} />
                            <Field k="Email" v={l.email} />
                            <Field k="Preferred contact" v={l.preferred_contact} />
                            <Field k="Service" v={l.service} />
                            <Field k="Moving date" v={l.moving_date || "Flexible"} />
                            <Field k="Flexible date" v={l.flexible_date ? "Yes" : "No"} />
                            <Field k="From" v={`${l.collection_postcode || "—"} (${l.collection_property_type || "—"}, floor ${l.collection_floor || "—"}, lift ${l.collection_lift ? "yes" : "no"})`} />
                            <Field k="To" v={`${l.delivery_postcode || "—"} (${l.delivery_property_type || "—"}, floor ${l.delivery_floor || "—"}, lift ${l.delivery_lift ? "yes" : "no"})`} />
                            <Field k="Bedrooms" v={l.bedrooms} />
                            <Field k="Boxes" v={l.boxes} />
                            <Field k="Large items" v={l.large_items || "—"} full />
                            <Field k="Dismantling" v={l.dismantling_required ? "Yes" : "No"} />
                            <Field k="Packing" v={l.packing_assistance ? "Yes" : "No"} />
                            <Field k="Parking" v={l.parking_info || "—"} full />
                            <Field k="Additional details" v={l.additional_details || "—"} full />
                        </div>
                    </div>

                    {data.images?.length > 0 && (
                        <div className="rounded-xl bg-white border border-[#17252D]/10 p-5">
                            <div className="font-semibold mb-3">Uploaded photos</div>
                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                                {data.images.map((img) => (
                                    <a key={img.id} href={absoluteUploadUrl(img.url)} target="_blank" rel="noreferrer" className="block rounded-lg overflow-hidden border border-[#17252D]/10">
                                        <img src={absoluteUploadUrl(img.url)} alt="" className="h-24 w-full object-cover" />
                                    </a>
                                ))}
                            </div>
                        </div>
                    )}

                    <div className="rounded-xl bg-white border border-[#17252D]/10 p-5">
                        <div className="font-semibold">Internal notes</div>
                        <div className="mt-2 space-y-2">
                            {data.notes?.length === 0 && <div className="text-sm text-[#17252D]/60">No notes yet.</div>}
                            {data.notes?.map((n) => (
                                <div key={n.id} className="rounded-lg border border-[#17252D]/10 p-3 text-sm">
                                    <div className="text-xs text-[#17252D]/60">{n.author} • {(n.created_at || "").slice(0, 16).replace("T", " ")}</div>
                                    <div className="mt-1 whitespace-pre-wrap">{n.body}</div>
                                </div>
                            ))}
                        </div>
                        <div className="mt-3 flex gap-2">
                            <Textarea value={noteBody} onChange={(e) => setNoteBody(e.target.value)} placeholder="Add an internal note…" rows={2} />
                            <Button onClick={addNote} className="bg-[#001B3D] hover:bg-[#001B3D]/90 text-white">Add note</Button>
                        </div>
                    </div>
                </div>

                <div className="space-y-4">
                    <div className="rounded-xl bg-white border border-[#17252D]/10 p-5">
                        <div className="font-semibold">Update status</div>
                        <div className="mt-2 space-y-2">
                            <Select value={status} onValueChange={setStatus}>
                                <SelectTrigger><SelectValue /></SelectTrigger>
                                <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                            </Select>
                            <Input placeholder="Note (optional)" value={statusNote} onChange={(e) => setStatusNote(e.target.value)} />
                            <Button onClick={updateStatus} className="w-full bg-[#001B3D] hover:bg-[#001B3D]/90 text-white">Save status</Button>
                        </div>
                    </div>
                    <div className="rounded-xl bg-white border border-[#17252D]/10 p-5">
                        <div className="font-semibold">Pricing</div>
                        <div className="mt-2 grid gap-2">
                            <div><Label>Quoted price (£)</Label><Input type="number" step="0.01" value={quotedPrice} onChange={(e) => setQuotedPrice(e.target.value)} /></div>
                            <div><Label>Booking value (£)</Label><Input type="number" step="0.01" value={bookingValue} onChange={(e) => setBookingValue(e.target.value)} /></div>
                            <Button onClick={savePricing} className="bg-[#001B3D] hover:bg-[#001B3D]/90 text-white">Save pricing</Button>
                        </div>
                    </div>
                    <div data-testid={ADMIN.timeline} className="rounded-xl bg-white border border-[#17252D]/10 p-5">
                        <div className="font-semibold">Status history</div>
                        <ol className="mt-3 relative pl-6 space-y-3">
                            {(data.history || []).map((h, i) => (
                                <li key={h.id || i} data-testid={ADMIN.timelineItem} className="relative">
                                    <span className="absolute -left-6 top-1.5 h-2.5 w-2.5 rounded-full bg-[#001B3D]" />
                                    <div className="text-sm"><span className="font-medium">{h.from_status || "—"}</span> → <span className="font-semibold">{h.to_status}</span></div>
                                    <div className="text-xs text-[#17252D]/60">{h.changed_by} • {(h.created_at || "").slice(0, 16).replace("T", " ")}</div>
                                    {h.note && <div className="text-xs mt-0.5">“{h.note}”</div>}
                                </li>
                            ))}
                        </ol>
                    </div>
                </div>
            </div>
        </div>
    );
}

function Field({ k, v, full }) {
    return (
        <div className={full ? "sm:col-span-2" : ""}>
            <div className="text-xs text-[#17252D]/60">{k}</div>
            <div className="font-medium break-words">{v || "—"}</div>
        </div>
    );
}
